import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart as RechartsPieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Alert, Badge, DataTable, Skeleton } from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import { SyncHealthOverviewWidget } from './SyncHealthOverviewWidget';
import {
  platformDashboardService,
  type PlatformStats,
  type PlatformRevenueTrend,
  type PlatformSummary,
  type PlatformAuditEvent,
  type HealthResponse,
} from '@/services/platform-dashboard.service';
import type { Tenant } from '@/types/tenant';
import {
  type ApprovalItem,
  type AuditEvent,
  type DateRange,
  type KpiItem,
  type KpiTone,
  type OperationalAlert,
  type SplitPoint,
  type TenantActivityItem,
  type TrendPoint,
  formatCurrency,
  formatNumber,
} from './platform-data';
import type { CurrencyCode } from '@bbpos/types';
import { formatPaise, formatPaiseCompact } from '@/utils/currency';

// ─── Design tokens ───────────────────────────────────────────────────────────
// Glass Depth: translucent backgrounds with backdrop blur and soft shadows
// No Borders: shadow + spacing as primary separators (no visible border-*)

const GLASS_PANEL =
  'rounded-2xl bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)] dark:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.5)]';

// ─── Utility ─────────────────────────────────────────────────────────────────

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

/** One figure per tenant currency ("₹689.59 · OMR9.629") — never a sum across currencies (P-008). */
function formatVolumes(volumes: Array<{ currency: CurrencyCode; revenue: number }>) {
  return volumes.length > 0
    ? volumes.map((v) => formatPaiseCompact(v.revenue, v.currency)).join(' · ')
    : '—';
}

function toneClasses(tone: KpiTone | 'info') {
  switch (tone) {
    case 'success':
      return {
        icon: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
        badge: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
        accent: 'from-emerald-500/20 to-emerald-500/0',
      };
    case 'warning':
      return {
        icon: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
        badge: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
        accent: 'from-amber-500/18 to-amber-500/0',
      };
    case 'danger':
      return {
        icon: 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400',
        badge: 'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300',
        accent: 'from-rose-500/18 to-rose-500/0',
      };
    case 'info':
      return {
        icon: 'bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400',
        badge: 'bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300',
        accent: 'from-sky-500/18 to-sky-500/0',
      };
    default:
      return {
        icon: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
        badge: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300',
        accent: 'from-indigo-500/18 to-indigo-500/0',
      };
  }
}

// ─── Icons ───────────────────────────────────────────────────────────────────

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35m1.85-5.15a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 2v4m8-4v4M4 10h16M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z" />
    </svg>
  );
}

function ExportIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v12m0 0 4-4m-4 4-4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    </svg>
  );
}

function ArrowTrendIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="m4 15 5-5 4 4 7-8" />
    </svg>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function SectionCard({
  title,
  eyebrow,
  description,
  action,
  children,
  className,
  contentClassName,
}: {
  title: string;
  eyebrow?: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
}) {
  return (
    <section className={cn(GLASS_PANEL, 'transition-shadow duration-200 hover:shadow-lg', className)}>
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-neutral-100/60 dark:border-neutral-800/60 px-5 py-4">
        <div className="min-w-0">
          {eyebrow && (
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500">
              {eyebrow}
            </p>
          )}
          <h2 className="text-base font-semibold tracking-tight text-neutral-900 dark:text-neutral-100">{title}</h2>
          {description && (
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">{description}</p>
          )}
        </div>
        {action}
      </div>
      <div className={cn('px-5 py-4', contentClassName)}>{children}</div>
    </section>
  );
}

function KpiCard({
  label,
  value,
  delta,
  supporting,
  tone,
}: {
  label: string;
  value: string;
  delta: string;
  supporting: string;
  tone: KpiTone;
}) {
  const palette = toneClasses(tone);

  return (
    <div className={cn(GLASS_PANEL, 'relative overflow-hidden p-5 transition-all duration-200 hover:shadow-lg')}>
      <div className={cn('absolute inset-x-0 top-0 h-16 bg-gradient-to-b', palette.accent)} />
      <div className="relative space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className={cn('flex h-11 w-11 items-center justify-center rounded-xl', palette.icon)}>
            <ArrowTrendIcon />
          </div>
          <span className={cn('rounded-full px-2.5 py-1 text-[11px] font-semibold', palette.badge)}>
            {delta}
          </span>
        </div>
        <div>
          <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{label}</p>
          <p className="mt-2 text-[28px] font-semibold tracking-tight text-neutral-950 dark:text-neutral-50 tabular-nums">
            {value}
          </p>
        </div>
        <p className="text-sm leading-6 text-neutral-500 dark:text-neutral-400">{supporting}</p>
      </div>
    </div>
  );
}

function EmptyPanelState({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex min-h-[160px] flex-col items-center justify-center rounded-2xl border border-dashed border-neutral-200/60 dark:border-neutral-700/60 bg-neutral-50/50 dark:bg-neutral-800/30 px-6 text-center">
      <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-300">{title}</p>
      <p className="mt-2 max-w-sm text-sm leading-6 text-neutral-500 dark:text-neutral-400">{description}</p>
    </div>
  );
}

function DateRangeButton({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded-full px-3 py-1.5 text-sm font-medium transition-all duration-150',
        active
          ? 'bg-indigo-600 text-white shadow-[0_8px_20px_-12px_rgba(79,70,229,0.9)]'
          : 'bg-white/80 dark:bg-neutral-800/80 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700',
      )}
    >
      {label}
    </button>
  );
}

function ApprovalPriorityBadge({ priority }: { priority: ApprovalItem['priority'] }) {
  const classes =
    priority === 'High'
      ? 'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300'
      : priority === 'Medium'
        ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
        : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300';

  return (
    <span className={cn('rounded-full px-2 py-1 text-[11px] font-semibold', classes)}>{priority}</span>
  );
}

function SeverityBadge({ severity }: { severity: OperationalAlert['severity'] | AuditEvent['severity'] }) {
  const classes =
    severity === 'critical'
      ? 'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300'
      : severity === 'warning'
        ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
        : severity === 'success'
          ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
          : 'bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300';

  return (
    <span className={cn('rounded-full px-2 py-1 text-[11px] font-semibold capitalize', classes)}>
      {severity}
    </span>
  );
}

// ─── Tenant DataTable columns ────────────────────────────────────────────────

const TENANT_STATUS_MAP: Record<string, 'success' | 'warning' | 'danger' | 'info'> = {
  Active: 'success',
  Trial: 'info',
  Pending: 'warning',
  Suspended: 'danger',
};

const tenantColumns: ColumnDef<TenantActivityItem>[] = [
  {
    key: 'tenantName',
    header: 'Tenant',
    width: '22%',
    render: (_v, row) => (
      <div>
        <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{row.tenantName}</p>
        <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">ID: {row.id}</p>
      </div>
    ),
  },
  {
    key: 'businessType',
    header: 'Type',
    width: '10%',
    render: (v) => (
      <span className="text-sm font-medium text-neutral-600 dark:text-neutral-300">{v as string}</span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    width: '10%',
    render: (v) => (
      <Badge variant={TENANT_STATUS_MAP[v as string] ?? 'info'}>{v as string}</Badge>
    ),
  },
  {
    key: 'locations',
    header: 'Locations',
    numeric: true,
    width: '10%',
    render: (v) => <span className="tabular-nums">{formatNumber(v as number)}</span>,
  },
  {
    key: 'lastActive',
    header: 'Last Active',
    width: '14%',
  },
  {
    key: 'mrr',
    header: 'MRR',
    numeric: true,
    width: '14%',
    render: (v) => (
      <span className="font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
        {(v as number) > 0 ? formatCurrency(v as number) : 'Trial'}
      </span>
    ),
  },
  {
    key: 'plan',
    header: 'Plan',
    width: '10%',
  },
];

// ─── Dashboard skeleton ──────────────────────────────────────────────────────

function DashboardSkeleton() {
  return (
    <div className="space-y-6" aria-label="Loading platform dashboard" role="status">
      <div className="grid gap-4 xl:grid-cols-[1.35fr_1fr]">
        <Skeleton variant="rect" height={140} />
        <Skeleton variant="rect" height={140} />
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton key={index} variant="rect" height={156} />
        ))}
      </div>
      <div className="grid gap-6 xl:grid-cols-12">
        <div className="xl:col-span-8">
          <Skeleton variant="rect" height={360} />
        </div>
        <div className="space-y-6 xl:col-span-4">
          <Skeleton variant="rect" height={180} />
          <Skeleton variant="rect" height={180} />
        </div>
      </div>
    </div>
  );
}

// ─── Data mapping helpers ────────────────────────────────────────────────────

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr ago`;
  const diffDay = Math.round(diffHr / 24);
  return `${diffDay}d ago`;
}

function normalizeTenantStatus(status: string): TenantActivityItem['status'] {
  switch (status) {
    case 'ACTIVE':
      return 'Active';
    case 'TRIAL':
      return 'Trial';
    case 'SUSPENDED':
      return 'Suspended';
    case 'PENDING_APPROVAL':
    case 'APPROVED':
    case 'PROVISIONED':
      return 'Pending';
    default:
      return 'Pending';
  }
}

function mapPendingTenantToApproval(tenant: Tenant): ApprovalItem {
  const ageHours = (Date.now() - new Date(tenant.created_at).getTime()) / 3_600_000;
  const priority: ApprovalItem['priority'] =
    ageHours > 2 ? 'High' : ageHours > 1 ? 'Medium' : 'Low';
  return {
    id: tenant.id,
    tenantName: tenant.business_name,
    requestType: 'Tenant activation',
    businessType: tenant.business_type === 'RETAIL' ? 'Retail' : 'Restaurant',
    priority,
    requestedAt: relativeTime(tenant.created_at),
    owner: tenant.owner_name ?? tenant.owner_email ?? '—',
    status: 'Awaiting review',
  };
}

function mapTenantToActivity(tenant: Tenant): TenantActivityItem {
  return {
    id: tenant.id,
    tenantName: tenant.business_name,
    businessType: tenant.business_type === 'RETAIL' ? 'Retail' : 'Restaurant',
    status: normalizeTenantStatus(tenant.status),
    locations: tenant.location_count ?? 0,
    lastActive: relativeTime(tenant.updated_at),
    mrr: 0,
    plan: tenant.subscription_plan ?? 'Trial',
  };
}

const AUDIT_ACTION_SEVERITY: Record<string, AuditEvent['severity']> = {
  CREATE: 'success',
  UPDATE: 'info',
  DELETE: 'critical',
  APPROVE: 'success',
  REJECT: 'warning',
  LOGIN: 'info',
  LOGOUT: 'info',
};

function mapAuditEvent(row: PlatformAuditEvent): AuditEvent {
  const actor = row.user_name ?? 'System';
  const action = (row.action ?? 'UPDATE').toLowerCase();
  const entity = row.entity_type ? row.entity_type.toLowerCase() : 'record';
  const suffix = row.entity_id ? ` ${row.entity_id.slice(0, 8)}` : '';
  return {
    id: String(row.id),
    actor,
    action,
    subject: `${entity}${suffix}`,
    timestamp: relativeTime(row.created_at),
    severity: AUDIT_ACTION_SEVERITY[(row.action ?? '').toUpperCase()] ?? 'info',
  };
}

function deriveOperationalAlerts(health: HealthResponse | null): OperationalAlert[] {
  if (!health) return [];
  const alerts: OperationalAlert[] = [];
  for (const [service, state] of Object.entries(health.checks)) {
    if (state === 'error') {
      alerts.push({
        id: `health-${service}`,
        title: `${service.charAt(0).toUpperCase() + service.slice(1)} check failing`,
        detail: `The ${service} health check returned an error. Investigate upstream connectivity and recent deploys.`,
        severity: 'critical',
        timestamp: `Updated ${relativeTime(new Date().toISOString())}`,
      });
    } else if (state === 'unavailable') {
      alerts.push({
        id: `health-${service}`,
        title: `${service.charAt(0).toUpperCase() + service.slice(1)} unavailable`,
        detail: `Health check for ${service} could not run. Module may be disabled or unreachable.`,
        severity: 'warning',
        timestamp: `Updated ${relativeTime(new Date().toISOString())}`,
      });
    }
  }
  if (alerts.length === 0) {
    alerts.push({
      id: 'health-ok',
      title: 'All systems operational',
      detail: 'Database, Redis, and RabbitMQ health checks are green. Uptime tracking is active.',
      severity: 'success',
      timestamp: `Updated ${relativeTime(new Date().toISOString())}`,
    });
  }
  return alerts;
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function PlatformDashboardContent() {
  const navigate = useNavigate();
  const profile = useAuthStore((state) => state.profile);
  const user = useAuthStore((state) => state.user);
  const [range, setRange] = useState<DateRange>('30d');
  const [searchQuery, setSearchQuery] = useState('');

  const [initialLoading, setInitialLoading] = useState(true);
  const [trendLoading, setTrendLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [summary, setSummary] = useState<PlatformSummary | null>(null);
  const [trend, setTrend] = useState<PlatformRevenueTrend | null>(null);
  const [chartCurrency, setChartCurrency] = useState<CurrencyCode | null>(null);
  const [pendingTenants, setPendingTenants] = useState<Tenant[]>([]);
  const [recentTenants, setRecentTenants] = useState<Tenant[]>([]);
  const [auditRows, setAuditRows] = useState<PlatformAuditEvent[]>([]);
  const [health, setHealth] = useState<HealthResponse | null>(null);

  // Initial load — fire everything in parallel
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [statsRes, summaryRes, trendRes, recentRes, pendingRes, auditRes, healthRes] =
          await Promise.all([
            platformDashboardService.getStats(),
            platformDashboardService.getSummary(),
            platformDashboardService.getRevenueTrend(range),
            platformDashboardService.getRecentTenants(),
            platformDashboardService.getPendingApprovals(),
            platformDashboardService.getAuditEvents(),
            platformDashboardService.getHealth().catch(() => null),
          ]);
        if (cancelled) return;
        setStats(statsRes);
        setSummary(summaryRes);
        setTrend(trendRes);
        setRecentTenants(recentRes.data);
        setPendingTenants(pendingRes.data);
        setAuditRows(auditRes.data);
        setHealth(healthRes);
      } catch (err) {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : 'Failed to load platform dashboard');
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // range intentionally not in dep list — trend refetch handled separately below
  }, []);

  // Refetch trend when the date range toggles
  useEffect(() => {
    if (initialLoading) return;
    let cancelled = false;
    setTrendLoading(true);
    platformDashboardService
      .getRevenueTrend(range)
      .then((res) => {
        if (!cancelled) setTrend(res);
      })
      .catch(() => {
        /* keep previous trend on error */
      })
      .finally(() => {
        if (!cancelled) setTrendLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range, initialLoading]);

  // ── Derived datasets ────────────────────────────────────────────────────
  // The chart has one money axis, so it shows one tenant currency at a time.
  const trendCurrencies = trend?.currencies ?? [];
  const trendCurrency: CurrencyCode =
    chartCurrency && trendCurrencies.includes(chartCurrency) ? chartCurrency : (trendCurrencies[0] ?? 'INR');
  const visibleRevenueSeries: TrendPoint[] = (trend?.points ?? []).map((p) => ({
    label: p.label,
    revenue: p.revenue[trendCurrency] ?? 0,
    previousRevenue: p.previousRevenue[trendCurrency] ?? 0,
    tenants: p.tenants,
  }));
  const totalLiveRevenue = trend?.total[trendCurrency] ?? 0;
  const totalPrevRevenue = trend?.previousTotal[trendCurrency] ?? 0;
  const revenueDelta = totalLiveRevenue - totalPrevRevenue;
  const filteredQuery = searchQuery.trim().toLowerCase();

  const businessSplit = useMemo<SplitPoint[]>(() => {
    if (!stats) return [];
    return [
      { name: 'Retail', value: stats.by_business_type.RETAIL, color: '#6366f1' },
      { name: 'Restaurant', value: stats.by_business_type.RESTAURANT, color: '#14b8a6' },
    ];
  }, [stats]);

  const businessSplitTotal = businessSplit.reduce((sum, p) => sum + p.value, 0);

  const operationalAlerts = useMemo(() => deriveOperationalAlerts(health), [health]);

  const kpiItems = useMemo<KpiItem[]>(() => {
    if (!stats || !summary) return [];
    const total = stats.totals.total;
    const active = stats.totals.active;
    const livePct = total > 0 ? ((active / total) * 100).toFixed(1) : '0';
    const openAlertsCount = operationalAlerts.filter(
      (a) => a.severity === 'critical' || a.severity === 'warning',
    ).length;
    const criticalCount = operationalAlerts.filter((a) => a.severity === 'critical').length;
    return [
      {
        id: 'total-tenants',
        label: 'Total tenants',
        value: formatNumber(total),
        delta: `+${formatNumber(stats.new_this_month)} this month`,
        tone: 'primary',
        supporting: `${formatNumber(stats.totals.pending_approval)} tenants awaiting activation review.`,
      },
      {
        id: 'active-tenants',
        label: 'Active tenants',
        value: formatNumber(active),
        delta: `${livePct}% live`,
        tone: 'success',
        supporting: `${formatNumber(stats.totals.trial)} trial and ${formatNumber(stats.totals.suspended)} suspended across the network.`,
      },
      {
        id: 'pending-approvals',
        label: 'Pending approvals',
        value: formatNumber(stats.totals.pending_approval),
        delta: `${formatNumber(pendingTenants.length)} in queue`,
        tone: 'warning',
        supporting: 'Activation, plan change, and suspension appeals awaiting review.',
      },
      {
        id: 'todays-volume',
        label: "Today's transaction volume",
        value: formatVolumes(summary.today.by_currency),
        delta: `${formatNumber(summary.today.transaction_count)} transactions`,
        tone: 'primary',
        supporting:
          summary.today.by_currency.length > 0
            ? `Retail drives ${summary.today.by_currency
                .map((v) => `${v.retail_share_percent}% of ${v.currency}`)
                .join(', ')} GMV today across the network.`
            : 'No completed bills yet today across the network.',
      },
      {
        id: 'open-alerts',
        label: 'Open alerts',
        value: formatNumber(openAlertsCount),
        delta: `${criticalCount} critical`,
        tone: openAlertsCount > 0 ? 'danger' : 'success',
        supporting:
          openAlertsCount > 0
            ? 'Sync, payments, and queue health need attention.'
            : 'All infrastructure checks reporting healthy.',
      },
    ];
  }, [stats, summary, pendingTenants, operationalAlerts]);

  const approvals = useMemo(() => pendingTenants.map(mapPendingTenantToApproval), [pendingTenants]);
  const tenantActivity = useMemo(
    () => recentTenants.map(mapTenantToActivity),
    [recentTenants],
  );
  const auditEvents = useMemo(() => auditRows.map(mapAuditEvent), [auditRows]);

  const filteredApprovals = useMemo(
    () =>
      approvals.filter((item) =>
        [item.tenantName, item.requestType, item.owner, item.businessType, item.status]
          .join(' ')
          .toLowerCase()
          .includes(filteredQuery),
      ),
    [filteredQuery, approvals],
  );

  const filteredTenants = useMemo(
    () =>
      tenantActivity.filter((item) =>
        [item.tenantName, item.businessType, item.status, item.plan]
          .join(' ')
          .toLowerCase()
          .includes(filteredQuery),
      ),
    [filteredQuery, tenantActivity],
  );

  const filteredAuditEvents = useMemo(
    () =>
      auditEvents.filter((item) =>
        [item.actor, item.action, item.subject].join(' ').toLowerCase().includes(filteredQuery),
      ),
    [filteredQuery, auditEvents],
  );

  const exportReport = useCallback(() => {
    const rows = filteredTenants.map((tenant) => ({
      tenant_name: tenant.tenantName,
      business_type: tenant.businessType,
      status: tenant.status,
      locations: tenant.locations,
      last_active: tenant.lastActive,
      plan: tenant.plan,
      mrr_inr: tenant.mrr,
    }));

    const csv = [
      Object.keys(
        rows[0] ?? {
          tenant_name: '',
          business_type: '',
          status: '',
          locations: '',
          last_active: '',
          plan: '',
          mrr_inr: '',
        },
      ).join(','),
      ...rows.map((row) => Object.values(row).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `orio360-platform-dashboard-${range}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [filteredTenants, range]);

  const scrollToAuditFeed = useCallback(() => {
    document.getElementById('audit-feed')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const currentDateLabel = useMemo(
    () =>
      new Intl.DateTimeFormat('en-IN', { dateStyle: 'full' }).format(new Date()),
    [],
  );

  if (initialLoading) {
    return <DashboardSkeleton />;
  }

  if (loadError) {
    return (
      <Alert
        variant="danger"
        title="Platform dashboard unavailable"
        description={loadError}
      />
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ── Hero + Command deck ─────────────────────────────────── */}
      <section className="grid gap-4 xl:grid-cols-[1.35fr_1fr]">
        <div className={cn(GLASS_PANEL, 'overflow-hidden')}>
          <div className="flex h-full flex-col justify-between gap-6 bg-[radial-gradient(circle_at_top_left,_rgba(99,102,241,0.14),_transparent_55%)] dark:bg-[radial-gradient(circle_at_top_left,_rgba(99,102,241,0.08),_transparent_55%)] px-6 py-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-neutral-400 dark:text-neutral-500">
                  Orio360 Admin Dashboard
                </p>
                <h1 className="mt-2 text-3xl font-semibold tracking-tight text-neutral-950 dark:text-neutral-50">
                  Platform control center
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-6 text-neutral-500 dark:text-neutral-400">
                  Monitor tenant health, approval workloads, infra signals, and revenue performance
                  across Retail and Restaurant operators from one operational surface.
                </p>
              </div>
              <div className="rounded-2xl bg-white/90 dark:bg-neutral-800/90 backdrop-blur-sm px-4 py-3 text-right shadow-sm">
                <p className="text-xs font-medium uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">
                  Snapshot
                </p>
                <p className="mt-1 text-sm font-semibold text-neutral-900 dark:text-neutral-100">{currentDateLabel}</p>
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">Live sync every 5 minutes</p>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-[1fr_auto]">
              <label className="group flex items-center gap-3 rounded-2xl bg-white/90 dark:bg-neutral-800/90 backdrop-blur-sm px-4 py-3 shadow-sm transition-colors focus-within:ring-2 focus-within:ring-indigo-400">
                <span className="text-neutral-400 dark:text-neutral-500 group-focus-within:text-indigo-500">
                  <SearchIcon />
                </span>
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search tenants, approvals, audit events"
                  className="w-full border-0 bg-transparent p-0 text-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:outline-none focus:ring-0"
                  aria-label="Global dashboard search"
                />
              </label>

              <div className="flex flex-wrap items-center gap-2 rounded-2xl bg-neutral-50/80 dark:bg-neutral-800/50 backdrop-blur-sm p-2">
                <span className="px-2 text-neutral-400 dark:text-neutral-500" aria-hidden="true">
                  <CalendarIcon />
                </span>
                <DateRangeButton active={range === '7d'} label="7D" onClick={() => setRange('7d')} />
                <DateRangeButton active={range === '30d'} label="30D" onClick={() => setRange('30d')} />
                <DateRangeButton active={range === '90d'} label="90D" onClick={() => setRange('90d')} />
              </div>
            </div>
          </div>
        </div>

        <div className={cn(GLASS_PANEL, 'p-5')}>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-neutral-400 dark:text-neutral-500">
            Command deck
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => navigate('/approvals')}
              className="rounded-2xl bg-indigo-600 px-4 py-3 text-left text-sm font-semibold text-white shadow-[0_14px_24px_-20px_rgba(79,70,229,1)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
            >
              Approve tenant
              <span className="mt-1 block text-xs font-medium text-indigo-100">
                Review the pending activation queue
              </span>
            </button>
            <button
              type="button"
              onClick={scrollToAuditFeed}
              className="rounded-2xl bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm px-4 py-3 text-left text-sm font-semibold text-neutral-900 dark:text-neutral-100 shadow-sm transition-all duration-200 hover:shadow-md"
            >
              View audits
              <span className="mt-1 block text-xs font-medium text-neutral-500 dark:text-neutral-400">
                Jump to recent platform change events
              </span>
            </button>
            <button
              type="button"
              onClick={() => navigate('/tenants/new')}
              className="rounded-2xl bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm px-4 py-3 text-left text-sm font-semibold text-neutral-900 dark:text-neutral-100 shadow-sm transition-all duration-200 hover:shadow-md"
            >
              Create tenant
              <span className="mt-1 block text-xs font-medium text-neutral-500 dark:text-neutral-400">
                Start a new onboarding record
              </span>
            </button>
            <button
              type="button"
              onClick={exportReport}
              className="rounded-2xl bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm px-4 py-3 text-left text-sm font-semibold text-neutral-900 dark:text-neutral-100 shadow-sm transition-all duration-200 hover:shadow-md"
            >
              <span className="flex items-center gap-2">
                <ExportIcon />
                Export report
              </span>
              <span className="mt-1 block text-xs font-medium text-neutral-500 dark:text-neutral-400">
                Download visible tenant activity as CSV
              </span>
            </button>
          </div>

          <div className="mt-4 rounded-2xl bg-neutral-50/60 dark:bg-neutral-800/40 backdrop-blur-sm p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">
              Current operator
            </p>
            <p className="mt-2 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              {profile?.name ?? user?.email ?? 'Platform Admin'}
            </p>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
              {profile?.role ?? user?.role ?? 'ADMIN'} - managing Retail and Restaurant tenants
            </p>
          </div>
        </div>
      </section>

      {/* ── KPI Cards ───────────────────────────────────────────── */}
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        {kpiItems.map((item) => (
          <KpiCard key={item.id} {...item} />
        ))}
      </section>

      {/* ── Revenue trend + Business split + Notifications ──────── */}
      <section className="grid gap-6 xl:grid-cols-12">
        <SectionCard
          title="Revenue and usage trend"
          eyebrow="Main analytics"
          description="GMV progression against the previous comparable window, with tenant count overlay."
          action={
            <div className="flex flex-wrap items-center gap-2">
              {trendCurrencies.length > 1 && (
                <div className="flex items-center gap-1 rounded-full bg-neutral-50/80 dark:bg-neutral-800/50 p-1" aria-label="Chart currency">
                  {trendCurrencies.map((c) => (
                    <DateRangeButton key={c} active={c === trendCurrency} label={c} onClick={() => setChartCurrency(c)} />
                  ))}
                </div>
              )}
              <div className={cn(
                'rounded-full px-3 py-1.5 text-sm font-semibold tabular-nums',
                revenueDelta >= 0
                  ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                  : 'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300',
                trendLoading && 'opacity-60',
              )}>
                {revenueDelta >= 0 ? '+' : '-'}
                {formatPaiseCompact(Math.abs(revenueDelta), trendCurrency)} vs prior window
              </div>
            </div>
          }
          className="xl:col-span-8"
          contentClassName="space-y-5"
        >
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="rounded-2xl bg-neutral-50/60 dark:bg-neutral-800/40 p-4">
              <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Current period GMV</p>
              <p className="mt-2 text-2xl font-semibold tracking-tight text-neutral-950 dark:text-neutral-50 tabular-nums">
                {formatPaiseCompact(totalLiveRevenue, trendCurrency)}
              </p>
            </div>
            <div className="rounded-2xl bg-neutral-50/60 dark:bg-neutral-800/40 p-4">
              <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Previous window</p>
              <p className="mt-2 text-2xl font-semibold tracking-tight text-neutral-950 dark:text-neutral-50 tabular-nums">
                {formatPaiseCompact(totalPrevRevenue, trendCurrency)}
              </p>
            </div>
            <div className="rounded-2xl bg-neutral-50/60 dark:bg-neutral-800/40 p-4">
              <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Live tenants tracked</p>
              <p className="mt-2 text-2xl font-semibold tracking-tight text-neutral-950 dark:text-neutral-50 tabular-nums">
                {formatNumber(visibleRevenueSeries[visibleRevenueSeries.length - 1]?.tenants ?? 0)}
              </p>
            </div>
          </div>

          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={visibleRevenueSeries} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity={0.22} />
                    <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="4 4" vertical={false} />
                <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  width={90}
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  tickFormatter={(value: number) => formatPaiseCompact(value, trendCurrency)}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: 16,
                    border: 'none',
                    boxShadow: '0 16px 30px -22px rgba(15,23,42,0.35)',
                    background: 'rgba(255,255,255,0.95)',
                    backdropFilter: 'blur(8px)',
                  }}
                  formatter={(value: number, name: string) => [
                    formatPaise(value, trendCurrency),
                    name === 'revenue' ? 'Current' : 'Previous',
                  ]}
                />
                <Area type="monotone" dataKey="previousRevenue" stroke="#cbd5e1" strokeWidth={2} strokeDasharray="6 6" fillOpacity={0} />
                <Area type="monotone" dataKey="revenue" stroke="#6366f1" strokeWidth={3} fill="url(#revenueFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <div className="space-y-6 xl:col-span-4">
          <SectionCard
            title="Business type split"
            eyebrow="Tenant mix"
            description="Current tenant footprint by vertical."
            contentClassName="space-y-4"
          >
            <div className="grid items-center gap-4 md:grid-cols-[168px_1fr]">
              <div className="mx-auto h-[168px] w-[168px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      data={businessSplit}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={48}
                      outerRadius={72}
                      strokeWidth={0}
                      paddingAngle={3}
                    >
                      {businessSplit.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => formatNumber(value)} />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-3">
                {businessSplit.map((item) => (
                  <div key={item.name} className="rounded-2xl bg-neutral-50/60 dark:bg-neutral-800/40 p-3">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <span className="h-3 w-3 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{item.name}</span>
                      </div>
                      <span className="text-sm font-semibold text-neutral-600 dark:text-neutral-300 tabular-nums">
                        {formatNumber(item.value)}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                      {businessSplitTotal > 0
                        ? `${Math.round((item.value / businessSplitTotal) * 100)}% of the platform footprint`
                        : 'No tenants onboarded yet'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </SectionCard>

          <SectionCard
            title="Platform pulse"
            eyebrow="Right rail"
            description="Live signals sourced from the tenant and billing stores."
            contentClassName="space-y-3"
          >
            <article className="rounded-2xl bg-neutral-50/60 dark:bg-neutral-800/40 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                    New tenants this month
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-neutral-500 dark:text-neutral-400">
                    {stats ? formatNumber(stats.new_this_month) : '—'} businesses onboarded since the month began.
                  </p>
                </div>
                <span className="rounded-full bg-indigo-50 dark:bg-indigo-900/30 px-2 py-1 text-[11px] font-semibold text-indigo-700 dark:text-indigo-300">
                  Growth
                </span>
              </div>
            </article>
            <article className="rounded-2xl bg-neutral-50/60 dark:bg-neutral-800/40 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                    Today's GMV
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-neutral-500 dark:text-neutral-400">
                    {summary
                      ? `${formatVolumes(summary.today.by_currency)} across ${formatNumber(summary.today.transaction_count)} transactions.`
                      : '—'}
                  </p>
                </div>
                <span className="rounded-full bg-emerald-50 dark:bg-emerald-900/30 px-2 py-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">
                  Live
                </span>
              </div>
            </article>
            <article className="rounded-2xl bg-neutral-50/60 dark:bg-neutral-800/40 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                    Approval backlog
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-neutral-500 dark:text-neutral-400">
                    {stats
                      ? `${formatNumber(stats.totals.pending_approval)} tenants awaiting platform review.`
                      : '—'}
                  </p>
                </div>
                <span className="rounded-full bg-amber-50 dark:bg-amber-900/30 px-2 py-1 text-[11px] font-semibold text-amber-700 dark:text-amber-300">
                  Queue
                </span>
              </div>
            </article>
          </SectionCard>
        </div>
      </section>

      {/* ── Tenant growth + Approval queue + Alerts ──────────────── */}
      <section className="grid gap-6 xl:grid-cols-12">
        <SectionCard
          title="Tenant growth"
          eyebrow="Acquisition"
          description="Onboarded tenant count over the selected reporting window."
          className="xl:col-span-5"
        >
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsBarChart data={visibleRevenueSeries} margin={{ top: 10, right: 0, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="#eef2f7" strokeDasharray="4 4" vertical={false} />
                <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                <YAxis tickLine={false} axisLine={false} width={45} tick={{ fill: '#64748b', fontSize: 12 }} />
                <Tooltip formatter={(value: number) => formatNumber(value)} />
                <Bar dataKey="tenants" fill="#6366f1" radius={[10, 10, 0, 0]} maxBarSize={52} />
              </RechartsBarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard
          title="Approval queue"
          eyebrow="Workflow"
          description="Activation, feature, plan, and suspension decisions waiting on platform action."
          className="xl:col-span-4"
          contentClassName="space-y-3"
          action={
            <button
              type="button"
              onClick={() => navigate('/approvals')}
              className="rounded-full bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm px-3 py-1.5 text-sm font-medium text-neutral-600 dark:text-neutral-300 shadow-sm transition-all duration-150 hover:shadow-md"
            >
              Open queue
            </button>
          }
        >
          {filteredApprovals.length === 0 ? (
            <EmptyPanelState
              title="No approval requests match the current search"
              description="Clear or broaden the search to review activation, plan, or suspension workflows."
            />
          ) : (
            filteredApprovals.map((item) => (
              <article key={item.id} className="rounded-2xl bg-neutral-50/60 dark:bg-neutral-800/40 p-4 transition-colors hover:bg-neutral-100/60 dark:hover:bg-neutral-700/40">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{item.tenantName}</h3>
                    <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">{item.requestType}</p>
                  </div>
                  <ApprovalPriorityBadge priority={item.priority} />
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2 text-xs font-medium text-neutral-500 dark:text-neutral-400">
                  <span className="rounded-full bg-white/80 dark:bg-neutral-700/80 px-2.5 py-1 text-neutral-700 dark:text-neutral-200">{item.businessType}</span>
                  <span className="rounded-full bg-white/80 dark:bg-neutral-700/80 px-2.5 py-1 text-neutral-700 dark:text-neutral-200">{item.status}</span>
                  <span>{item.owner}</span>
                  <span className="text-neutral-300 dark:text-neutral-600">/</span>
                  <span>{item.requestedAt}</span>
                </div>
              </article>
            ))
          )}
        </SectionCard>

        <SectionCard
          title="Operational alerts"
          eyebrow="Platform health"
          description="Sync, payment, queue, and infrastructure notices."
          className="xl:col-span-3"
          contentClassName="space-y-3"
        >
          {operationalAlerts.map((alert) => (
            <article key={alert.id} className="rounded-2xl bg-neutral-50/60 dark:bg-neutral-800/40 p-4 transition-colors hover:bg-neutral-100/60 dark:hover:bg-neutral-700/40">
              <div className="flex items-center justify-between gap-3">
                <SeverityBadge severity={alert.severity} />
                <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">
                  {alert.timestamp}
                </span>
              </div>
              <h3 className="mt-3 text-sm font-semibold text-neutral-900 dark:text-neutral-100">{alert.title}</h3>
              <p className="mt-1 text-sm leading-6 text-neutral-500 dark:text-neutral-400">{alert.detail}</p>
            </article>
          ))}
        </SectionCard>
      </section>

      {/* ── Sync Health Overview (G2.26) — full-width cross-tenant widget ── */}
      <section className="grid gap-6 xl:grid-cols-12">
        <div className="xl:col-span-12">
          <SyncHealthOverviewWidget />
        </div>
      </section>

      {/* ── Tenant activity table + Audit feed ──────────────────── */}
      <section className="grid gap-6 xl:grid-cols-12">
        <SectionCard
          title="Recent tenant activity"
          eyebrow="Tenant operations"
          description="Latest live status across the most active platform accounts."
          className="xl:col-span-8"
          contentClassName="p-0"
          action={
            <div className="rounded-full bg-neutral-100/80 dark:bg-neutral-800/60 px-3 py-1.5 text-sm font-medium text-neutral-600 dark:text-neutral-300 tabular-nums">
              {filteredTenants.length} visible
            </div>
          }
        >
          {filteredTenants.length === 0 ? (
            <div className="px-5 py-5">
              <EmptyPanelState
                title="No tenants match the current search"
                description="Try a tenant name, plan, or status such as Active, Pending, or Suspended."
              />
            </div>
          ) : (
            <DataTable
              columns={tenantColumns}
              data={filteredTenants}
              variant="compact"
              emptyState={{
                title: 'No tenants found',
                description: 'Adjust your search or filters.',
              }}
            />
          )}
        </SectionCard>

        <div id="audit-feed" className="space-y-6 xl:col-span-4">
          <SectionCard
            title="Recent audit events"
            eyebrow="Secondary rail"
            description="Approval and platform events captured in the append-only audit log."
            contentClassName="space-y-3"
          >
            {filteredAuditEvents.length === 0 ? (
              <EmptyPanelState
                title="No audit events match the search"
                description="Search by actor or action to jump back into a specific operational trail."
              />
            ) : (
              filteredAuditEvents.map((event) => (
                <article key={event.id} className="rounded-2xl bg-neutral-50/60 dark:bg-neutral-800/40 p-4 transition-colors hover:bg-neutral-100/60 dark:hover:bg-neutral-700/40">
                  <div className="flex items-center justify-between gap-3">
                    <SeverityBadge severity={event.severity} />
                    <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">
                      {event.timestamp}
                    </span>
                  </div>
                  <p className="mt-3 text-sm leading-6 text-neutral-600 dark:text-neutral-300">
                    <span className="font-semibold text-neutral-900 dark:text-neutral-100">{event.actor}</span> {event.action}{' '}
                    {event.subject}.
                  </p>
                </article>
              ))
            )}
          </SectionCard>

          <SectionCard
            title="System activity feed"
            eyebrow="Live ops"
            description="A compact feed for the admin team's current operating posture."
            contentClassName="space-y-3"
          >
            <div className="rounded-2xl bg-neutral-50/60 dark:bg-neutral-800/40 p-4">
              <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Socket rooms healthy</p>
              <p className="mt-1 text-sm leading-6 text-neutral-500 dark:text-neutral-400">
                Tenant and role rooms are broadcasting without backlog after the 14:05 autoscale event.
              </p>
            </div>
            <div className="rounded-2xl bg-neutral-50/60 dark:bg-neutral-800/40 p-4">
              <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Approval throughput improving</p>
              <p className="mt-1 text-sm leading-6 text-neutral-500 dark:text-neutral-400">
                Median review time is now 28 minutes, down 11% from the previous 7-day window.
              </p>
            </div>
            <div className="rounded-2xl bg-neutral-50/60 dark:bg-neutral-800/40 p-4">
              <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Audit consumer backlog normal</p>
              <p className="mt-1 text-sm leading-6 text-neutral-500 dark:text-neutral-400">
                Queue depth stabilized below the 50-event threshold after the latest replay batch.
              </p>
            </div>
          </SectionCard>
        </div>
      </section>
    </div>
  );
}
